/**
 * MyGiving PIN step-up session (VAPT 2026-10-07 findings 1 + 3).
 *
 * A correct PIN makes hogc-one-api open a server-side PIN session and return its
 * id. The app sends that id as X-Giving-Pin-Session on giving-api calls, which
 * guards bank linking, recurring changes and large FAST payments with it.
 *
 * The session ends when the member goes back to the homepage, never in the middle
 * of a transaction (bank redirect, switching to a banking app). The server still
 * expires an idle session after 15 minutes; the app mirrors that on resume so a
 * phone left in the background re-asks for the PIN instead of failing a payment.
 *
 * Contract: ~/hogc-stg-ops/pin-step-up-contract.md (giving-api + hogc-one-api).
 */

export const PIN_SESSION_HEADER = 'X-Giving-Pin-Session';

/**
 * Base URL for the /giving/pin/* calls: the hogc-one-api that shares a Redis with
 * the giving-api this build talks to, so giving-api can see the PIN session.
 *
 * Staging and production builds leave VUE_APP_GIVING_PIN_API_URL unset and use
 * their own hogc-one-api (VUE_APP_API_URL). Dev builds set it to staging
 * hogc-one-api, because their giving calls already go to staging giving-api and
 * the dev account's Redis is out of its reach.
 */
export const givingPinApiUrl = (env = process.env) =>
  env.VUE_APP_GIVING_PIN_API_URL || env.VUE_APP_API_URL;

/** Sent on /giving/pin/state when no session is held, so the server uses session rules. */
export const NO_PIN_SESSION = 'none';

/** Mirrors the server's sliding idle TTL (900 s). */
export const PIN_SESSION_IDLE_MS = 15 * 60 * 1000;

/** giving-api answers 403 with this code when a guarded call has no valid PIN session. */
export const PIN_REQUIRED_CODE = 'PIN_REQUIRED';

export const HOME_PATH = '/tabs/home';
export const MY_GIVING_PATH = '/my-giving';

/** Going home is what ends a PIN session. */
export const endsPinSession = to => to?.path === HOME_PATH;

/**
 * A MyGiving page that sits behind the PIN gate (mirrors router/guards/mygiving-pin-guard.js).
 * @param {string} path
 */
export const isPinGatedPath = path =>
  typeof path === 'string' &&
  path.startsWith(`${MY_GIVING_PATH}/`) &&
  !path.startsWith(`${MY_GIVING_PATH}/pin/`) &&
  path !== `${MY_GIVING_PATH}/bo-redirection`;

/** Where to send the member to re-enter the PIN, coming back to `fullPath` afterwards. */
export const pinPromptLocation = fullPath => ({
  path: MY_GIVING_PATH,
  query: { redirect: fullPath },
  replace: true,
});

/** Was the app in the background longer than the server keeps an idle session? */
export const pinSessionIdledOut = (pausedAt, now = Date.now()) =>
  typeof pausedAt === 'number' && now - pausedAt > PIN_SESSION_IDLE_MS;

/**
 * router.afterEach hook: going home ends the PIN session.
 * @param {{ state: object, dispatch: Function }} store
 */
export const createEndPinSessionOnHome = store => to => {
  const { pinVerifiedThisSession, pinSessionId } = store.state.myGiving || {};
  if (endsPinSession(to) && (pinVerifiedThisSession || pinSessionId)) {
    store.dispatch('myGiving/endPinSession').catch(() => {});
  }
};

/**
 * Capacitor pause/resume handling. Pausing never ends the session (the member may
 * be in their banking app mid-transaction); resuming after the server's idle
 * window does, and sends a member on a PIN-gated page back to the PIN screen.
 *
 * @param {{ store: { dispatch: Function }, router: object, now?: () => number }} deps
 */
export function createPinSessionLifecycle({ store, router, now = () => Date.now() }) {
  let pausedAt = null;
  return {
    onPause() {
      pausedAt = now();
    },
    onResume() {
      const idledOut = pinSessionIdledOut(pausedAt, now());
      pausedAt = null;
      if (!idledOut) return false;
      store.dispatch('myGiving/endPinSession').catch(() => {});
      const current = router.currentRoute.value;
      if (isPinGatedPath(current.path)) {
        router.replace(pinPromptLocation(current.fullPath)).catch(() => {});
      }
      return true;
    },
  };
}
